import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { checkAuth, logoutUser, deleteAccount, registerDeviceToken, setAuthToken } from '../services/api';
import { clearPendingLogins } from '../services/spotifyAuth';
import { initPushNotifications } from '../services/pushNotifications';
import { isNative, platform } from '../utils/platform';

const LNAuthContext = createContext(null);

export function LNAuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const initAuth = async () => {
      try {
        setIsLoading(true);
        const result = await checkAuth();

        if (result.authenticated && result.user) {
          setUser(result.user);
          // Init push notifications after confirming auth
          initPushNotifications((token) => registerDeviceToken(token, platform));
        } else {
          setUser(null);
        }
      } catch (err) {
        console.error('LN Auth check failed:', err);
        setUser(null);
      } finally {
        setIsLoading(false);
        if (isNative) {
          import('@capacitor/splash-screen').then(({ SplashScreen }) => {
            SplashScreen.hide().catch(() => {});
          });
        }
      }
    };

    initAuth();
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (err) {
      console.error('LN Logout error:', err);
    } finally {
      setUser(null);
      setAuthToken(null);
      clearPendingLogins();
    }
  }, []);

  const deleteAccountAndSignOut = useCallback(async () => {
    await deleteAccount();
    setUser(null);
    setAuthToken(null);
    clearPendingLogins();
  }, []);

  const value = {
    user,
    setUser,
    isLoading,
    setIsLoading,
    error,
    setError,
    logout,
    deleteAccount: deleteAccountAndSignOut,
    isAuthenticated: !!user,
  };

  return (
    <LNAuthContext.Provider value={value}>
      {children}
    </LNAuthContext.Provider>
  );
}

export function useLNAuth() {
  const context = useContext(LNAuthContext);
  if (!context) {
    throw new Error('useLNAuth must be used within a LNAuthProvider');
  }
  return context;
}
